import { ICheckedForecast } from "store/features";

import { ArpSegment } from "models/UserArpsModel";

/**
 * Helper to check if a segment is the first occurrence of its product in an arps array.
 * Used to identify which segments should have their start dates cascaded.
 *
 * @param segments - Array of arps segments
 * @param segment - The segment to check
 * @param index - The index of the segment in the array
 * @returns True if this is the first occurrence of the product
 */
export function isFirstProductSegment(
  segments: ArpSegment[],
  segment: ArpSegment,
  index: number
): boolean {
  return segments.findIndex((s) => s.product === segment.product) === index;
}

/**
 * Cascades the start date to the first segment of each product.
 * Only updates the first occurrence of each product in the arps array.
 * Note: This assumes segments are ordered correctly (segmentIndex 1 appears first for each product).
 *
 * @param newDate - The new start date (string or Date object)
 * @param currentForecast - The current forecast data
 * @returns Updated forecast with cascaded start dates
 */
export function cascadeStartDateToAllProducts(
  newDate: string | Date,
  currentForecast: ICheckedForecast
): ICheckedForecast {
  if (!currentForecast || !currentForecast.arps) {
    return currentForecast;
  }

  const dateValue = typeof newDate === "string" ? newDate : newDate.toISOString();

  // Update the start date for the first occurrence of each product
  const seenProducts = new Set<string>();
  const updatedArps = currentForecast.arps.map((segment) => {
    if (!seenProducts.has(segment.product)) {
      seenProducts.add(segment.product);
      return {
        ...segment,
        startDate: dateValue
      };
    }

    return segment;
  });

  return {
    ...currentForecast,
    arps: updatedArps
  };
}
